/** SUNTRONIX ONE — Bulk Inward Wizard.
 *
 *  Three-step guided flow that turns one PO into N Golden Asset Records
 *  in a single submit.  Solves the P1 backlog item "Bulk Inward Wizard".
 *
 *  Steps:
 *    1. PO & product template — one form for the whole batch.
 *    2. Serials — paste-friendly textarea, one per line.
 *    3. Review & submit — confirm counts, product, dedupe view, submit.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, ArrowRight, Check, Loader2, PackageOpen,
} from "lucide-react";

const CATEGORIES = ["Laptop", "Desktop", "Mobile", "Tablet", "Monitor", "Peripheral", "Server", "Network", "Accessory", "Other"];
const GRADES = ["A", "B", "C", "D"];
const POOLS = ["new_stock", "ready_to_deploy", "redeployment_stock", "spare_pool"];
const AVAIL = ["available_new", "ready_to_deploy", "ready_for_redeployment"];

export default function BulkInward() {
  const navigate = useNavigate();
  const { user } = useAuth?.() || { user: {} };
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [tpl, setTpl] = useState({
    po_reference: "",
    brand: "",
    model: "",
    category: "Laptop",
    subcategory: "",
    condition_grade: "A",
    inventory_pool: "new_stock",
    availability: "available_new",
    country: "",
    warehouse: "",
    purchase_date: "",
    warranty_end: "",
    purchase_price: "",
    currency: "USD",
  });
  const [rawSerials, setRawSerials] = useState("");
  const [master, setMaster] = useState(user?.master_company_id || "");

  const parsedSerials = useMemo(() => {
    const set = new Set();
    const clean = [];
    for (const line of (rawSerials || "").split(/\r?\n|,|;/)) {
      const s = line.trim();
      if (!s || set.has(s)) continue;
      set.add(s);
      clean.push(s);
    }
    return clean;
  }, [rawSerials]);

  const canGoNext = () => {
    if (step === 1) return tpl.brand && tpl.model && tpl.category && master;
    if (step === 2) return parsedSerials.length > 0;
    return true;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        master_company_id: master,
        po_reference: tpl.po_reference || null,
        brand: tpl.brand,
        model: tpl.model,
        category: tpl.category,
        subcategory: tpl.subcategory || null,
        condition_grade: tpl.condition_grade,
        inventory_pool: tpl.inventory_pool,
        availability: tpl.availability,
        custody_type: "suntronix_warehouse",
        ownership_type: "customer",
        country: tpl.country || null,
        warehouse: tpl.warehouse || null,
        purchase_date: tpl.purchase_date || null,
        warranty_end: tpl.warranty_end || null,
        purchase_price: tpl.purchase_price ? Number(tpl.purchase_price) : null,
        currency: tpl.currency || "USD",
        serials: parsedSerials,
      };
      const { data } = await api.post("/customer-inventory/bulk-inward", payload);
      toast.success(`${data.created} technology asset${data.created === 1 ? "" : "s"} inwarded from ${data.po_reference || "the PO"}.`);
      navigate("/inventory");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk inward failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[960px] space-y-6" data-testid="bulk-inward">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Inventory · Bulk inward</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
          <PackageOpen className="h-5 w-5 text-slate-500" strokeWidth={1.6} /> Bulk Inward Wizard
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-[70ch]">
          Intake one PO of many serials in a single guided flow.  Product template applies to every serial;
          duplicates within the tenant are rejected up-front.
        </p>
      </div>

      {/* Stepper */}
      <ol className="flex items-center gap-4 text-[12px]" data-testid="bulk-inward-steps">
        {[
          { n: 1, label: "PO & template" },
          { n: 2, label: "Serials" },
          { n: 3, label: "Review & submit" },
        ].map((s) => (
          <li key={s.n} className={"flex items-center gap-2 " + (step >= s.n ? "text-slate-900" : "text-slate-400")}>
            <span className={"h-6 w-6 grid place-items-center rounded-full text-[11px] font-semibold " +
              (step > s.n ? "bg-emerald-500 text-white" : step === s.n ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500")}>
              {step > s.n ? <Check className="h-3.5 w-3.5" /> : s.n}
            </span>
            <span>{s.label}</span>
          </li>
        ))}
      </ol>

      {/* Step content */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        {step === 1 && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="bulk-inward-step-template">
            <FieldWrap label="Master company ID" required>
              <Input value={master} onChange={(e) => setMaster(e.target.value)} placeholder="mc_…" data-testid="bi-master" />
            </FieldWrap>
            <FieldWrap label="PO reference">
              <Input value={tpl.po_reference} onChange={(e) => setTpl({ ...tpl, po_reference: e.target.value })} placeholder="PO-2026-…" data-testid="bi-po" />
            </FieldWrap>
            <FieldWrap label="Brand" required>
              <Input value={tpl.brand} onChange={(e) => setTpl({ ...tpl, brand: e.target.value })} placeholder="Apple / Dell / HP …" data-testid="bi-brand" />
            </FieldWrap>
            <FieldWrap label="Model" required>
              <Input value={tpl.model} onChange={(e) => setTpl({ ...tpl, model: e.target.value })} placeholder="MacBook Pro 14 / Latitude 7440 …" data-testid="bi-model" />
            </FieldWrap>
            <FieldWrap label="Category" required>
              <Select value={tpl.category} onValueChange={(v) => setTpl({ ...tpl, category: v })}>
                <SelectTrigger className="h-10" data-testid="bi-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap label="Subcategory">
              <Input value={tpl.subcategory} onChange={(e) => setTpl({ ...tpl, subcategory: e.target.value })} placeholder="Optional" />
            </FieldWrap>
            <FieldWrap label="Condition grade">
              <Select value={tpl.condition_grade} onValueChange={(v) => setTpl({ ...tpl, condition_grade: v })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>Grade {g}</SelectItem>)}</SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap label="Inventory pool">
              <Select value={tpl.inventory_pool} onValueChange={(v) => setTpl({ ...tpl, inventory_pool: v })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{POOLS.map((p) => <SelectItem key={p} value={p}><span className="capitalize">{p.replace(/_/g, " ")}</span></SelectItem>)}</SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap label="Availability">
              <Select value={tpl.availability} onValueChange={(v) => setTpl({ ...tpl, availability: v })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{AVAIL.map((a) => <SelectItem key={a} value={a}><span className="capitalize">{a.replace(/_/g, " ")}</span></SelectItem>)}</SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap label="Warehouse">
              <Input value={tpl.warehouse} onChange={(e) => setTpl({ ...tpl, warehouse: e.target.value })} placeholder="e.g. Mumbai · Rack B-12" />
            </FieldWrap>
            <FieldWrap label="Country">
              <Input value={tpl.country} onChange={(e) => setTpl({ ...tpl, country: e.target.value })} placeholder="IN / US / SG …" />
            </FieldWrap>
            <FieldWrap label="Purchase date">
              <Input type="date" value={tpl.purchase_date} onChange={(e) => setTpl({ ...tpl, purchase_date: e.target.value })} />
            </FieldWrap>
            <FieldWrap label="Warranty end">
              <Input type="date" value={tpl.warranty_end} onChange={(e) => setTpl({ ...tpl, warranty_end: e.target.value })} />
            </FieldWrap>
            <FieldWrap label="Purchase price (per unit)">
              <Input type="number" value={tpl.purchase_price} onChange={(e) => setTpl({ ...tpl, purchase_price: e.target.value })} placeholder="0.00" />
            </FieldWrap>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="bulk-inward-step-serials">
            <Label className="text-xs">Paste serial numbers — one per line (or separated by commas / semicolons).</Label>
            <Textarea
              value={rawSerials}
              onChange={(e) => setRawSerials(e.target.value)}
              placeholder={"SN-000001\nSN-000002\nSN-000003"}
              className="min-h-[280px] font-mono text-[13px]"
              data-testid="bi-serials"
            />
            <div className="text-[12px] text-slate-500 flex items-center gap-3">
              <span data-testid="bi-serials-count"><b className="text-slate-900">{parsedSerials.length}</b> unique serial{parsedSerials.length === 1 ? "" : "s"}</span>
              {parsedSerials.length > 0 && (
                <span>· first: <span className="font-mono">{parsedSerials[0]}</span></span>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 text-[13px]" data-testid="bulk-inward-step-review">
            <div className="grid md:grid-cols-3 gap-4">
              <ReviewCell label="PO reference" value={tpl.po_reference || "—"} />
              <ReviewCell label="Serials" value={`${parsedSerials.length}`} />
              <ReviewCell label="Product" value={`${tpl.brand} ${tpl.model}`} />
              <ReviewCell label="Category" value={tpl.category} />
              <ReviewCell label="Grade" value={tpl.condition_grade} />
              <ReviewCell label="Pool · Availability" value={`${tpl.inventory_pool.replace(/_/g, " ")} · ${tpl.availability.replace(/_/g, " ")}`} />
              <ReviewCell label="Warehouse" value={tpl.warehouse || "—"} />
              <ReviewCell label="Warranty end" value={tpl.warranty_end || "—"} />
              <ReviewCell label="Unit price" value={tpl.purchase_price ? `${tpl.currency} ${tpl.purchase_price}` : "—"} />
            </div>
            <div className="border border-slate-200 rounded-md p-3 max-h-[180px] overflow-auto text-[12px] font-mono">
              {parsedSerials.slice(0, 100).map((s, i) => (
                <div key={s} className="flex items-center gap-3 py-0.5">
                  <span className="text-slate-400 w-8 text-right">{i + 1}.</span>
                  <span className="text-slate-800">{s}</span>
                </div>
              ))}
              {parsedSerials.length > 100 && (
                <div className="text-slate-500 pt-2">… and {parsedSerials.length - 100} more.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => step === 1 ? navigate("/inventory") : setStep(step - 1)}
          data-testid="bi-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> {step === 1 ? "Cancel" : "Back"}
        </Button>
        {step < 3 ? (
          <Button
            className="bg-slate-900 hover:bg-slate-800"
            disabled={!canGoNext()}
            onClick={() => setStep(step + 1)}
            data-testid="bi-next"
          >
            Next <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        ) : (
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={submitting || parsedSerials.length === 0}
            onClick={submit}
            data-testid="bi-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
            Inward {parsedSerials.length} asset{parsedSerials.length === 1 ? "" : "s"}
          </Button>
        )}
      </div>
    </div>
  );
}

function FieldWrap({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs">{label}{required && <span className="text-rose-500"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ReviewCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-900 font-medium">{value}</div>
    </div>
  );
}
